import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import styles from './flyer-text-element.scss'

const MIN_FONT_SIZE = 5
const FlyerTextElement = ({ flyerTexts, ratio }) => {
  let textEls = []
  const minFontSizeRef = useRef(MIN_FONT_SIZE)

  useEffect(() => {
    const minFontSize = Math.max(MIN_FONT_SIZE, getBrowserMinFontSize())
    minFontSizeRef.current = minFontSize
    for (let el of textEls) {
      adjustSize(el, minFontSizeRef.current)
    }
  }, [flyerTexts])

  const adjustSize = async (el, minFontSize) => {
    const adjustWidth = await new Promise(resolve => {
      adjustSizeWithWidth(el, minFontSize, resolve)
    })

    adjustSizeWithHeight(el, minFontSize, adjustWidth.ratio)
  }

  const getBrowserMinFontSize = () => {
    const testDiv = document.createElement('div')
    testDiv.style.position = 'absolute'
    testDiv.style.visibility = 'hidden'
    testDiv.style.fontSize = '1px'
    testDiv.innerText = 'A'
    document.body.appendChild(testDiv)
    const computedSize = window.getComputedStyle(testDiv).fontSize
    document.body.removeChild(testDiv)
    return Number(computedSize.replace('px', ''))
  }

  const adjustSizeWithWidth = (el, minFontSize, resolve) => {
    const parentEl = el.parentNode
    const parentWidth = parentEl.offsetWidth
    const width = el.offsetWidth
    const align = getComputedStyle(parentEl)['text-align']
    if (width > parentWidth) {
      const size = Number(getComputedStyle(parentEl)['font-size'].replace('px', ''))
      const whiteSpace = getComputedStyle(el)['white-space']
      const justifyContent = getComputedStyle(parentEl)['justify-content']
      if (size <= minFontSize) {
        const ratio = 1 - (width - parentWidth) / width
        if (align === 'center') {
          if (whiteSpace === 'pre' || whiteSpace === 'pre-wrap') {
            if (el.scrollWidth <= parentEl.offsetWidth) {
              el.style.transformOrigin = 'center top'
              el.style.transform = `scale(${ratio})`
            } else {
              const scaleRatio = parentEl.offsetWidth / el.scrollWidth
              el.style.transformOrigin = 'left top'
              el.style.transform = `translateX(${(parentEl.offsetWidth - el.scrollWidth * scaleRatio) / 2}px) scale(${scaleRatio})`
            }
          } else {
            el.style.transformOrigin = 'center top'
            el.style.transform = `scale(${ratio})`
          }
        } else {
          el.style.transformOrigin = 'left top'
          el.style.transform = `scale(${ratio})`
        }

        if (align === 'right' && justifyContent === 'flex-end') {
          el.style.transformOrigin = 'right bottom'
        }

        resolve({ status: true, ratio })
        return
      } else {
        parentEl.style.fontSize = size - 0.3
      }
      adjustSizeWithWidth(el, minFontSize, resolve)
    } else {
      resolve({ status: true })
    }
  }

  const adjustSizeWithHeight = (el, minFontSize, widthRatio) => {
    const parentEl = el.parentNode
    const parentHeight = parentEl.offsetHeight
    const ratio = widthRatio || 1
    const height = el.offsetHeight
    const currentHeight = height * ratio

    if (currentHeight > parentHeight) {
      const size = Number(getComputedStyle(parentEl)['font-size'].replace('px', ''))
      if (size <= minFontSize) {
        const align = getComputedStyle(parentEl)['text-align']

        const heightRatio = ratio - (currentHeight - parentHeight) / height
        el.style.transform = `scale(${heightRatio})`
        el.style.transformOrigin = 'left top'

        if (align === 'center') {
          const currentWidth = el.offsetWidth * heightRatio
          const translateX = (parentEl.offsetWidth - currentWidth) / 2
          el.style.transform = `translateX(${translateX}px) scale(${heightRatio})`
        }

        if (align === 'right') {
          if (widthRatio) {
            const currentWidth = el.offsetWidth * heightRatio
            const translateX = parentEl.offsetWidth - currentWidth
            el.style.transform = `translateX(${translateX}px) scale(${heightRatio})`
          } else {
            el.style.transformOrigin = 'right top'
          }
        }

        return
      } else {
        parentEl.style.fontSize = size - 0.3
      }

      adjustSizeWithHeight(el, minFontSize)
    }
  }

  const renderTextEl = (item, index) => {
    let fontSize = item.size * ratio
    let scaleRatio = 1

    let textStyle = {
      fontFamily: item.font,
      fontSize: item.size * ratio,
      top: item.top * ratio,
      color: item.color,
      width: item.width * ratio
    }

    if (item.transformOrigin) {
      scaleRatio = fontSize / minFontSizeRef.current
      textStyle.transform = `scale(${scaleRatio})`
      textStyle.transformOrigin = item.transformOrigin
    }

    if (item.height) {
      textStyle.height = item.height * ratio
    }

    if (item.lineHeight) {
      textStyle.lineHeight = item.lineHeight ? item.lineHeight * ratio + 'px' : undefined
    }

    if (item.whiteSpace) {
      textStyle.whiteSpace = item.whiteSpace
    }

    if (item.nowrap) {
      textStyle.whiteSpace = 'nowrap'
    }

    if (item.align === 'right') {
      textStyle.right = item.right ? item.right * ratio : 0
      if (item.widthAlign === 'center') {
        textStyle.textAlign = 'center'
      } else {
        textStyle.textAlign = 'right'
      }
    } else if (item.align === 'center') {
      textStyle.left = '50%'
      textStyle.transform = `translateX(-50%) scale(${scaleRatio})`
      textStyle.transformOrigin = 'center top'
      textStyle.textAlign = 'center'
    } else {
      if (item.widthAlign === 'center') {
        textStyle.textAlign = 'center'
      } else {
        textStyle.textAlign = 'left'
      }
      textStyle.left = item.left * ratio
    }

    if (item.verticalAlign) {
      textStyle.display = 'flex'
      if (item.align === 'center') {
        textStyle.justifyContent = 'center'
      } else if (item.align === 'right') {
        textStyle.justifyContent = 'flex-end'
      } else {
        textStyle.justifyContent = 'flex-start'
      }
      if (item.verticalAlign === 'bottom') {
        textStyle.alignItems = 'flex-end'
      } else if (item.verticalAlign === 'center') {
        textStyle.alignItems = 'center'
      } else {
        textStyle.alignItems = 'flex-start'
      }
    }

    let textDescriptionStyle = {}
    if (item.overflow) {
      textDescriptionStyle.overflow = 'hidden'
      textDescriptionStyle.textOverflow = 'ellipsis'
      textDescriptionStyle.whiteSpace = 'nowrap'
      textDescriptionStyle.display = '-webkit-box'
      textDescriptionStyle.WebkitLineClamp = item.overflow
      textDescriptionStyle.WebkitBoxOrient = 'vertical'
    }

    return (
      <div className={styles['text-box']} style={textStyle} key={index}>
        <div className={styles['text']} style={textDescriptionStyle} ref={ref => ref && textEls.push(ref)}>

          {item.textParts
            ? item.textParts.map((part, idx) => (
              <span
                key={idx}
                style={{
                  fontFamily: part.font || item.font,
                  color: part.color || item.color,
                  fontSize: (part.size || item.size) * ratio,
                  whiteSpace: 'pre'
                }}
              >
                {part.text}
              </span>
            ))
            : item.text}
        </div>
      </div>
    )
  }

  return (
    <div className={styles['flyer-text']}>
      {flyerTexts.map((item, index) => {
        if (item.type === 'date') {
          return item.dates.map((date, i) => {
            return renderTextEl(date, i)
          })
        }
        return renderTextEl(item, index)
      })}
    </div>
  )
}

FlyerTextElement.propTypes = {
  ratio: PropTypes.number,
  flyerTexts: PropTypes.array
}

export default FlyerTextElement
